import React, { useMemo, useState } from 'react';
import './InterpretationDetailModal.css';

const JsonTreeViewer = ({ data }) => {
  if (data === null || data === undefined) {
    return <span className="json-null">null</span>;
  }

  if (Array.isArray(data)) {
    if (data.length === 0) {
      return <span className="json-empty">[]</span>;
    }

    return (
      <ol className="json-array">
        {data.map((item, index) => (
          <li key={index}>
            <span className="json-index">[{index}]</span>
            <JsonTreeViewer data={item} />
          </li>
        ))}
      </ol>
    );
  }

  if (typeof data === 'object') {
    const entries = Object.entries(data);
    if (entries.length === 0) {
      return <span className="json-empty">{}</span>;
    }

    return (
      <ul className="json-object">
        {entries.map(([key, value]) => (
          <li key={key}>
            <span className="json-key">{key}:</span>
            <JsonTreeViewer data={value} />
          </li>
        ))}
      </ul>
    );
  }

  let valueClass = 'json-string';
  if (typeof data === 'number') valueClass = 'json-number';
  if (typeof data === 'boolean') valueClass = 'json-boolean';

  return <span className={valueClass}>{JSON.stringify(data)}</span>;
};

const toSentence = (value) => {
  if (!value || typeof value !== 'string') {
    return value;
  }
  return value.replace(/_/g, ' ').replace(/\b([a-z])/g, (match) => match.toUpperCase());
};

const formatNumber = (value) => {
  if (value === null || value === undefined) {
    return '�';
  }
  if (typeof value === 'number') {
    return value.toFixed(2);
  }
  return value;
};

function InterpretationDetailModal({ isOpen, onClose, interpretationData }) {
  const [copied, setCopied] = useState(false);

  const deepQueryData = useMemo(
    () => interpretationData?.deepQuery ?? interpretationData ?? null,
    [interpretationData]
  );
  const baseVariantData = interpretationData?.baseVariant ?? null;

  const { tier_decision, decision_support } = deepQueryData || {};
  const identity = decision_support?.variant_identity ?? {};

  const tier = tier_decision?.final_tier || 'Not Assigned';
  const flags = tier_decision?.flags || {};
  const categories = tier_decision?.categories || [];
  const advisoryNotes = tier_decision?.guideline_advisory || [];
  const conflicts = tier_decision?.conflicts || [];
  const overallScore = tier_decision?.overall_score;

  const clinicalSignificance = decision_support?.clinical_significance || [];
  const therapies = decision_support?.therapeutic_implications?.oncokb_fda_approved_treatments || [];
  const civicEvidence = decision_support?.therapeutic_implications?.civic_literature_evidence || [];
  const trials = decision_support?.clinical_trials?.active_trials || [];

  const handleCopyAll = async () => {
    try {
      const baseCopy = baseVariantData ? { ...baseVariantData } : null;
      if (baseCopy && baseCopy.deepQueryResult) {
        try {
          delete baseCopy.deepQueryResult;
        } catch (_) {
          // ignore
        }
      }
      const payload = {
        variant: baseCopy,
        deep_query: deepQueryData,
      };

      const text = JSON.stringify(payload, null, 2);
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Failed to copy data:', err);
    }
  };

  if (!isOpen || !deepQueryData) {
    return null;
  }

  const activeFlags = Object.entries(flags)
    .filter(([, value]) => Boolean(value))
    .map(([key]) => toSentence(key));

  const metaFacts = [
    { label: 'Genomic Location', value: identity?.genomic_location },
    { label: 'Alteration', value: identity?.alteration || identity?.full_hgvs },
    { label: 'HGVS', value: identity?.full_hgvs },
    { label: 'Variant Class', value: deepQueryData?.after_enrichment?.variant_class },
    { label: 'Allele String', value: deepQueryData?.after_enrichment?.allele_string },
  ].filter((item) => item.value);

  const classificationBadges = clinicalSignificance.map((item) => `${item.source}: ${item.value}`);

  const trialSummary = trials.slice(0, 3).map((trial) => ({
    title: trial.title,
    info: [trial.phase, trial.status, trial.nctId || trial.nct_id].filter(Boolean).join(' � '),
  }));

  return (
    <div className="idm-modal-backdrop" onClick={onClose}>
      <div className="idm-modal-content" onClick={(event) => event.stopPropagation()}>
        <div className="idm-modal-header">
          <div className="idm-header-text">
            <div className="idm-header-title">
              {identity?.gene || 'Variant'}
              {identity?.alteration ? <span className="idm-header-sub">{identity.alteration}</span> : null}
            </div>
            <div className="idm-header-meta">
              {identity?.rs_id ? <span>{identity.rs_id}</span> : null}
              {identity?.genomic_location ? <span>{identity.genomic_location}</span> : null}
              {identity?.variant_class ? <span>{identity.variant_class}</span> : null}
            </div>
          </div>
          <div className="idm-actions">
            <button className="idm-copy-btn" onClick={handleCopyAll} disabled={copied}>
              {copied ? 'Copied' : 'Copy All'}
            </button>
            <button className="idm-modal-close" onClick={onClose}>
              <span aria-hidden>&times;</span>
              <span className="idm-sr-only">Close</span>
            </button>
          </div>
        </div>

        <div className="idm-modal-body">
          <div className="idm-summary-grid">
            <div className="idm-summary-card">
              <div className="idm-summary-label">Tier</div>
              <div className="idm-summary-primary">{tier}</div>
              <div className="idm-summary-secondary">{tier_decision?.summary || '�'}</div>
            </div>
            <div className="idm-summary-card">
              <div className="idm-summary-label">Overall Score</div>
              <div className="idm-summary-primary">{formatNumber(overallScore)}</div>
              <div className="idm-summary-secondary">
                {tier_decision?.audit?.length ? `${tier_decision.audit.length} scoring steps` : 'No audit log'}
              </div>
            </div>
            <div className="idm-summary-card">
              <div className="idm-summary-label">Insights</div>
              <div className="idm-summary-pill-group">
                {classificationBadges.length > 0 ? (
                  classificationBadges.map((label) => (
                    <span className="idm-pill" key={label}>
                      {label}
                    </span>
                  ))
                ) : (
                  <span className="idm-summary-secondary">No classifications supplied</span>
                )}
              </div>
            </div>
          </div>

          {metaFacts.length > 0 && (
            <div className="idm-meta-grid">
              {metaFacts.map((item) => (
                <div className="idm-meta-item" key={item.label}>
                  <div className="idm-meta-label">{item.label}</div>
                  <div className="idm-meta-value">{item.value}</div>
                </div>
              ))}
            </div>
          )}

          {activeFlags.length > 0 && (
            <div className="idm-section">
              <div className="idm-section-header">Highlights</div>
              <div className="idm-pill-row">
                {activeFlags.map((flag) => (
                  <span className="idm-pill" key={flag}>
                    {flag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {categories.length > 0 && (
            <div className="idm-section">
              <div className="idm-section-header">Tier Breakdown</div>
              <div className="idm-category-grid">
                {categories.map((category, index) => (
                  <div className="idm-category-card" key={`${category.category}-${index}`}>
                    <div className="idm-category-header">
                      <span>{category.category}</span>
                      <span className="idm-category-score">Score {category.score}</span>
                    </div>
                    {Array.isArray(category.table) && category.table.length > 0 && (
                      <ul className="idm-category-table">
                        {category.table.map((row, idx) => (
                          <li key={idx}>
                            <span className="idm-table-value">{row.value}</span>
                            {row.source ? <span className="idm-table-source">{row.source}</span> : null}
                          </li>
                        ))}
                      </ul>
                    )}
                    {Array.isArray(category.breakdown) && category.breakdown.length > 0 && (
                      <ul className="idm-category-breakdown">
                        {category.breakdown.map((item, idx) => (
                          <li key={idx}>{item.text}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {therapies.length > 0 && (
            <div className="idm-section">
              <div className="idm-section-header">FDA Approved Therapies</div>
              <ul className="idm-simple-list">
                {therapies.map((therapy, index) => {
                  const details = [therapy.cancerType, therapy.evidenceLevel]
                    .filter(Boolean)
                    .join(' � ');

                  return (
                    <li key={`${therapy.drugs || 'therapy'}-${index}`}>
                      <div className="idm-list-primary">{therapy.drugs || '�'}</div>
                      {details ? <div className="idm-list-secondary">{details}</div> : null}
                      {therapy.fdaApproval ? (
                        <div className="idm-list-description">{therapy.fdaApproval}</div>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {civicEvidence.length > 0 && (
            <div className="idm-section">
              <div className="idm-section-header">Literature Evidence</div>
              <ul className="idm-simple-list">
                {civicEvidence.map((item, index) => {
                  const evidenceLine = [item.disease, item.evidence_type, item.evidence_level]
                    .filter(Boolean)
                    .join(' � ');

                  return (
                    <li key={`${item.gene}-${item.pubmed_id || index}`}>
                      <div className="idm-list-primary">
                        {item.gene} � {item.variant}
                      </div>
                      {evidenceLine ? <div className="idm-list-secondary">{evidenceLine}</div> : null}
                      {item.pubmed_id ? (
                        <div className="idm-list-description">PubMed: {item.pubmed_id}</div>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {trialSummary.length > 0 && (
            <div className="idm-section">
              <div className="idm-section-header">Highlighted Trials</div>
              <ul className="idm-simple-list">
                {trialSummary.map((trial, index) => (
                  <li key={`${trial.title}-${index}`}>
                    <div className="idm-list-primary">{trial.title}</div>
                    {trial.info ? <div className="idm-list-secondary">{trial.info}</div> : null}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {advisoryNotes.length > 0 && (
            <div className="idm-section">
              <div className="idm-section-header">Guideline Advisory</div>
              <ul className="idm-bullet-list">
                {advisoryNotes.map((note, idx) => (
                  <li key={idx}>{note}</li>
                ))}
              </ul>
            </div>
          )}

          {conflicts.length > 0 && (
            <div className="idm-section">
              <div className="idm-section-header">Conflicts</div>
              <ul className="idm-bullet-list">
                {conflicts.map((conflict, idx) => (
                  <li key={idx}>{conflict}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="idm-json-columns">
            <div className="idm-json-panel">
              <div className="idm-json-header">Variant JSON</div>
              <div className="idm-json-body">
                {baseVariantData ? (
                  <JsonTreeViewer data={baseVariantData} />
                ) : (
                  <span className="idm-muted">No base variant data provided.</span>
                )}
              </div>
            </div>
            <div className="idm-json-panel">
              <div className="idm-json-header">Deep Query JSON</div>
              <div className="idm-json-body">
                <JsonTreeViewer data={deepQueryData} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default InterpretationDetailModal;
